/**
 * "NEW" on the outreacher's folder chips: a folder released in the last few
 * days that this outreacher hasn't opened yet.
 *
 * Opened folders are remembered in browser storage, per outreacher (an admin
 * impersonating two people on one browser mustn't mix them up). Storage can
 * be missing or throw (private mode, blocked site data); then nothing is
 * remembered across visits and only the age rule applies.
 */

/** A folder counts as new for this long after its latest release. */
export const NEW_FOLDER_DAYS = 3;
/** Opened folder ids kept per outreacher; the oldest drop off first. */
export const SEEN_FOLDERS_CAP = 200;

const DAY_MS = 24 * 60 * 60 * 1000;
const KEY_PREFIX = 'spade-queue-seen-folders:';

export function seenFoldersStorageKey(inviterId: number): string {
  return `${KEY_PREFIX}${inviterId}`;
}

/** Released less than NEW_FOLDER_DAYS ago. A missing or unparseable date is never new. */
export function isRecentlyReleased(lastExportedAt: string | null | undefined, nowMs: number): boolean {
  if (!lastExportedAt) return false;
  const t = Date.parse(lastExportedAt);
  if (!Number.isFinite(t)) return false;
  return nowMs - t < NEW_FOLDER_DAYS * DAY_MS;
}

/** Folder ids this outreacher has opened, oldest first; null when storage can't be read. */
export function readSeenFolders(inviterId: number): string[] | null {
  try {
    const raw = window.localStorage.getItem(seenFoldersStorageKey(inviterId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return null;
  }
}

/** `seen` with `key` moved to the end (most recent), capped to the newest `cap`. */
export function withSeenFolder(seen: ReadonlyArray<string>, key: string, cap: number = SEEN_FOLDERS_CAP): string[] {
  return [...seen.filter((x) => x !== key), key].slice(-cap);
}

/** Remember that this outreacher opened `key`. Silently does nothing if storage is unavailable. */
export function markFolderSeen(inviterId: number, key: string): void {
  try {
    const current = readSeenFolders(inviterId) ?? [];
    if (current[current.length - 1] === key) return;
    window.localStorage.setItem(seenFoldersStorageKey(inviterId), JSON.stringify(withSeenFolder(current, key)));
  } catch {
    // The chip still clears for this visit; it just won't be remembered.
  }
}
